import axios, { AxiosError, AxiosResponse } from 'axios';
import { authStore } from '../store/authStore';

/**
 * API URL Configuration
 * REACT_APP_API_URL should be the base domain only (e.g. https://api.clicktahn.com).
 * The /api path is always appended here so it can never be accidentally omitted.
 */
function getApiUrl(): string {
  const base = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000')
    .replace(/\/+$/, '')   // strip trailing slashes
    .replace(/\/api$/, ''); // strip /api if someone included it — we add it below
  return `${base}/api`;
}

const API_BASE_URL = getApiUrl();

console.log('[API Client] Configured API URL:', API_BASE_URL);

/**
 * Set right before the one auto-reload below, so App.tsx can show a
 * plain-language explanation after the reload instead of the page just
 * changing out from under the user with no context. Cleared by whoever
 * consumes it via consumeSessionExpiredNotice() -- see that function.
 */
const SESSION_EXPIRED_RELOAD_FLAG = 'session_expired_reload';

/** Call once on app mount to check (and clear) whether the last reload was
 * triggered by an unrecoverable session expiry, so a notice can be shown
 * exactly once. */
export function consumeSessionExpiredNotice(): boolean {
  if (typeof window === 'undefined') return false;
  const wasSet = sessionStorage.getItem(SESSION_EXPIRED_RELOAD_FLAG) === '1';
  if (wasSet) sessionStorage.removeItem(SESSION_EXPIRED_RELOAD_FLAG);
  return wasSet;
}

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor - Add JWT token to headers
 */
apiClient.interceptors.request.use(
  (config) => {
    // List of public endpoints that don't need authentication
    // Note: /professionals/ and /clinics/ were deliberately removed from this list —
    // those endpoints are AllowAny but also serve authenticated dashboard fetches
    // (e.g. a clinic member loading their own inactive/private clinic), which need
    // the token attached to be recognized as authenticated by the backend's
    // queryset filtering. Anonymous visitors have no token, so omitting them here
    // doesn't change anonymous behavior.
    const publicEndpoints = [
      '/users/verify-email/',
      '/users/resend-verification/',
      '/users/request-password-reset/',
      '/users/reset-password/',
      '/token/',
    ];
    
    // Check if this is a public endpoint (for GET requests)
    const isPublicEndpoint = publicEndpoints.some(endpoint => 
      config.url?.includes(endpoint)
    ) && config.method?.toLowerCase() === 'get';
    
    // Only add token for non-public endpoints
    if (!isPublicEndpoint) {
      const token = authStore.getState().accessToken;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        console.log('[API Request] Added Authorization header');
      }
    } else {
      console.log('[API Request] Skipping Authorization for public endpoint:', config.url);
    }
    
    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

/**
 * Shared in-flight refresh so concurrent 401s (e.g. several queued offline
 * writes replaying at once right after reconnecting) all await the same
 * network call instead of each independently POSTing to /token/refresh/.
 * That matters because the backend rotates refresh tokens on every use
 * (SIMPLE_JWT ROTATE_REFRESH_TOKENS + BLACKLIST_AFTER_ROTATION): a second
 * concurrent call reading the same not-yet-rotated refresh token from the
 * store races the first, and once persistence is fixed below (saving the
 * *new* refresh token, not just the access token) a second later call
 * with the old, now-blacklisted token would otherwise fail outright and
 * force-logout an otherwise perfectly healthy session -- confirmed via a
 * real write_queue failure (401 on first sync attempt right at reconnect,
 * succeeded on retry) and a reported unexpected logout on reconnect.
 */
let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  if (refreshPromise) {
    return refreshPromise;
  }
  refreshPromise = (async () => {
    const refreshToken = authStore.getState().refreshToken;
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    const response = await axios.post(`${API_BASE_URL}/token/refresh/`, {
      refresh: refreshToken,
    });
    const { access, refresh } = response.data;
    // Persist BOTH tokens -- the backend always returns a freshly rotated
    // refresh token alongside the access token (rotation is enabled), and
    // discarding it here is what left the store holding a stale,
    // already-blacklisted refresh token for the *next* refresh attempt.
    authStore.getState().setTokens({ access, refresh });
    return access;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

/**
 * Response interceptor - Handle token refresh and errors
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log('[API Response] Success:', response.config.url, response.status);
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    console.error('[API Response Error]', {
      url: error.config?.url,
      status: error.response?.status,
      data: error.response?.data,
    });

    // Handle 401 (Unauthorized) - Try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      console.log('[API] Attempting token refresh...');

      try {
        const access = await refreshAccessToken();
        console.log('[API] Token refreshed successfully');

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        console.error('[API] Token refresh failed', refreshError);
        authStore.getState().logout();
        // Refresh only reaches here when the refresh token itself is dead
        // (expired past its own 1-day lifetime, or blacklisted -- e.g. this
        // session got evicted by the 2-concurrent-session cap when the user
        // logged in elsewhere). logout() only clears in-memory/localStorage
        // auth state; it doesn't touch whatever's already rendered, so a
        // *public* page (not behind PrivateRoute, which reacts to
        // isAuthenticated on its own) is left showing components built
        // around now-failed queries with no mechanism to retry them anon.
        // A hard reload is the simplest guaranteed-clean recovery: tokens
        // are already cleared, so the reload's requests go out with no
        // Authorization header and public pages work again immediately,
        // while protected pages fall through PrivateRoute to /login as
        // normal. Set a one-shot flag first so the user gets a plain-language
        // explanation instead of the page just silently reloading on them.
        if (typeof window !== 'undefined' && !sessionStorage.getItem(SESSION_EXPIRED_RELOAD_FLAG)) {
          sessionStorage.setItem(SESSION_EXPIRED_RELOAD_FLAG, '1');
          window.location.reload();
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
